import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useCustomers, useProjects } from '../hooks/useInfra'

// The customer context of the operator area: a chosen customer filters the
// infrastructure lists to its resources (machines, networks, VMs, jobs).
const Ctx = createContext<{ customerId: string; setCustomerId: (id: string) => void }>({ customerId: '', setCustomerId: () => {} })
const KEY = 'xc.customer'

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customerId, set] = useState(() => {
    try { return localStorage.getItem(KEY) || '' } catch { return '' }
  })
  const setCustomerId = (id: string) => {
    set(id)
    try { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY) } catch { /* private mode */ }
  }
  return <Ctx.Provider value={{ customerId, setCustomerId }}>{children}</Ctx.Provider>
}

export function useCustomerContext() {
  return useContext(Ctx)
}

// useCustomerFilter: matchProject tells whether a resource of a project
// belongs to the chosen customer (always true without a choice).
export function useCustomerFilter() {
  const { customerId, setCustomerId } = useContext(Ctx)
  const { data: projects } = useProjects()
  const { data: customers } = useCustomers()
  return useMemo(() => {
    const ids = new Set((projects || []).filter((p) => p.customer_id === customerId).map((p) => p.id))
    const customer = customers?.find((c) => c.id === customerId)
    // a remembered customer that no longer exists filters nothing
    const active = !!customer
    return {
      customerId,
      customer,
      active,
      clear: () => setCustomerId(''),
      matchProject: (pid?: string | null) => !active || (!!pid && ids.has(pid)),
    }
  }, [customerId, projects, customers, setCustomerId])
}
